import React from "react";
import { ICardElementWithChildren } from "backend/model/model";
import typeToView from "routing/typetoview";
import { getYearFromElementId, matchesAnyStringFields } from "./searchUtils";

const SEARCH_FIELDS: Array<keyof ICardElementWithChildren> = [
  "name",
  "rangename",
  "periodlevelname",
  "description",
  "typename",
  "scorecardname",
  "scorecardsymbol",
];

export const useSearchResults = (
  data: ICardElementWithChildren[] | undefined,
  isLoading: boolean,
  searchCandidate: string,
  modelId?: string
) => {
  const [results, setResults] = React.useState<ICardElementWithChildren[] | undefined>();

  React.useEffect(() => {
    if (!data || isLoading) return;
    if (!searchCandidate || searchCandidate.trim().length === 0) {
      setResults(undefined);
      return;
    }

    let res: ICardElementWithChildren[] = data;
    const tokens = searchCandidate.split(" ").filter(Boolean);

    tokens.forEach((searchKey) => {
      res = res
        .filter(
          (v) =>
            typeToView(v.typename) &&
            !v.scorecardname?.includes("_config") &&
            !v.symbol?.includes("D0")
        )
        .filter((v) => matchesAnyStringFields(SEARCH_FIELDS, v, searchKey));
    });

    setResults(res);
  }, [data, searchCandidate, isLoading, modelId]);

  const groupedResults = React.useMemo(() => {
    if (!results || results.length === 0) return null;

    const grouped: Record<string, ICardElementWithChildren[]> = {};

    results.forEach((item) => {
      if (!item || !item.id) return;
      const year = getYearFromElementId(item.id) || "Unknown";
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(item);
    });

    if (Object.keys(grouped).length === 0) return null;

    const sortedYears = Object.keys(grouped).sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return parseInt(b, 10) - parseInt(a, 10);
    });

    return { grouped, sortedYears };
  }, [results]);

  return { results, groupedResults };
};

